/*
 * Copyright (c) CIRROM 2026.
 */

import { toPng } from "html-to-image";

/*
 * Share and copy-as-image, ported from the suggested-changes modal. The
 * exported card carries the Cirrom mark in its header, so every image that
 * leaves the product travels with the brand on it.
 */

/** What is being shared; drives the subject line and the body's nouns. */
export type ShareEntity = "contact" | "account";

export interface ShareDetails {
  entity: ShareEntity;
  /** The record's display name: a person for a contact, a company for an account. */
  name: string;
  company?: string | null;
  jobTitle?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  location?: string | null;
}

/**
 * Render the card element to a PNG and place it on the clipboard.
 * Throws when rendering or clipboard access fails - callers own the alert.
 */
export const copyRecordImageToClipboard = async (
  element: HTMLElement,
  backgroundColor: string
): Promise<void> => {
  const dataUrl = await toPng(element, { cacheBust: true, backgroundColor });
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
};

/**
 * Pre-filled mailto for sharing a record. The image itself cannot ride along
 * in a mailto, so the body says whether it is already on the clipboard or has
 * to be copied separately.
 */
export const buildShareEmailUrl = (
  details: ShareDetails,
  imageOnClipboard: boolean
): string => {
  const noun = details.entity;
  const Noun = noun === "contact" ? "Contact" : "Account";
  const subject = encodeURIComponent(`Cirrom ${Noun}: ${details.name}`);

  let body = `Hi there,\n\nI wanted to share this ${noun} record from Cirrom:\n\n`;
  body += `Name: ${details.name}\n`;
  if (details.company) body += `Company: ${details.company}\n`;
  if (details.jobTitle) body += `Job Title: ${details.jobTitle}\n`;
  if (details.email) body += `Email: ${details.email}\n`;
  if (details.phone) body += `Phone: ${details.phone}\n`;
  if (details.website) body += `Website: ${details.website}\n`;
  if (details.location) body += `Location: ${details.location}\n`;
  body += `\n--- Detailed Record Data ---\n\n`;
  if (imageOnClipboard) {
    body += `Please find the detailed ${noun} record attached as an image. The image shows all CRM and Cirrom suggested data for easy reference.\n\n`;
    body += `Note: The ${noun} record image has been copied to your clipboard. Please paste it as an attachment in your email.\n\n`;
    body += `Please let me know if you have any questions or need additional context.\n\n`;
  } else {
    body += `Please use the separate copy button to get the ${noun} record image for attachment.\n\n`;
  }
  body += `Best regards`;

  return `mailto:?subject=${subject}&body=${encodeURIComponent(body)}`;
};
